import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { FormatPackagingUnitPrice, FormatPackagingVolumePrice, round } from '@kheops/utils';
import { Base_Unit_Type_Enum, Billing_Type_Enum } from '@kheops/graphql';
import { companyPackagingsAtom } from '../state';
import { usePackagingsByCompanyQuery } from '../../queries/__generated__/packagingsByCompany.generated';
import { currentContextAtom } from '../../state';
import { BaseUnitPricesCollection, DraftPackagingsPrices } from './state';

export interface UsePackagingsPricesFormDefaultValues {
  defaultValues: DraftPackagingsPrices;
  ready: boolean;
}

export default function usePackagingsPricesFormDefaultValues(): UsePackagingsPricesFormDefaultValues {
  const { companyId } = useAtomValue(currentContextAtom);
  const [companyPackagings, setCompanyPackagings] = useAtom(companyPackagingsAtom);
  const { data: companyPackagingsData, loading } = usePackagingsByCompanyQuery({
    variables: {
      companyId,
      skuPackagingsToIgnore: [],
    },
  });

  useEffect((): void => {
    if (companyPackagingsData?.packaging?.length) {
      setCompanyPackagings(companyPackagingsData?.packaging);
    }
  }, [companyPackagingsData]);

  const defaultValues = useMemo((): DraftPackagingsPrices => {
    return {
      prices: companyPackagings.reduce((acc, packaging) => {
        const isPackagingBilledByWeight = packaging.base_unit.billing_type === Billing_Type_Enum.WeightDependent;
        const isBulk = packaging.base_unit.unit_type === Base_Unit_Type_Enum.Bulk;
        const packagingUnitPrice = isPackagingBilledByWeight || isBulk
          ? FormatPackagingVolumePrice(packaging, packaging.base_unit).value
          : FormatPackagingUnitPrice(packaging);
        const baseUnitSku = packaging.base_unit.sku!;
        const suggestedRetailPrice = packaging.base_unit.suggested_retail_price || undefined;
        const packagingPrices = {
          packagingPrice: packaging.price,
          packagingUnitPrice: round(packagingUnitPrice, 3),
        };

        if (!acc[baseUnitSku]) {
          acc[baseUnitSku] = {
            suggestedRetailPrice,
            packagings: {
              [packaging.sku]: packagingPrices,
            },
          };
        } else {
          acc[baseUnitSku].packagings[packaging.sku] = packagingPrices;
        }

        return acc;
      }, {} as BaseUnitPricesCollection),
    };
  }, [companyPackagings]);

  return {
    defaultValues,
    ready: !loading,
  };
}
